import { useQuery } from "@tanstack/react-query";
import { toast } from "@/components/ui/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import { ActiveBidsTable, Bid } from "@/components/admin/bids/ActiveBidsTable";
import { ActiveBidsTableSkeleton } from "@/components/admin/bids/ActiveBidsTableSkeleton";
import { EventFilterComponent } from "@/components/admin/events/EventFilterComponent";
import { useState, useMemo, useCallback, useEffect } from "react";
import { TimeFilterEnum, MANAGE_BIDS_CONFIG } from "@/types/eventFilter";
import { EventStatusEnum } from "@/types/eventStatus";
import { AdminEventSummaryDTO } from "@/pages/admin/events/types";
import { useDebounce } from "@/hooks/useDebounce";
import { DataTablePagination } from "@/components/ui/data-table-pagination";

export default function ManageBids() {
  const [sortColumn, setSortColumn] = useState<keyof Bid | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [selectedStatuses, setSelectedStatuses] = useState<EventStatusEnum[]>(
    MANAGE_BIDS_CONFIG.defaultSelectedStatuses || [],
  );
  const [companyName, setCompanyName] = useState<string>("");
  const [contactName, setContactName] = useState<string>("");
  const [hasVacancies, setHasVacancies] = useState<boolean | undefined>(
    undefined,
  );
  const [timeFilter, setTimeFilter] = useState<TimeFilterEnum | undefined>(
    undefined,
  );
  const [startDateFrom, setStartDateFrom] = useState<string>("");
  const [startDateTo, setStartDateTo] = useState<string>("");
  const [endDateFrom, setEndDateFrom] = useState<string>("");
  const [endDateTo, setEndDateTo] = useState<string>("");

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(0);

  const debouncedCompanyName = useDebounce(companyName, 500);
  const debouncedContactName = useDebounce(contactName, 500);

  // Reset page when any filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [
    selectedStatuses,
    debouncedCompanyName,
    debouncedContactName,
    hasVacancies,
    timeFilter,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
  ]);

  const params = new URLSearchParams();
  if (selectedStatuses.length > 0) {
    selectedStatuses.forEach((status) => params.append("statuses", status));
  }
  if (startDateFrom) params.append("startDateFrom", startDateFrom);
  if (startDateTo) params.append("startDateTo", startDateTo);
  if (endDateFrom) params.append("endDateFrom", endDateFrom);
  if (endDateTo) params.append("endDateTo", endDateTo);
  if (debouncedContactName.trim())
    params.append("contactName", debouncedContactName.trim());
  if (debouncedCompanyName.trim())
    params.append("companyName", debouncedCompanyName.trim());
  if (hasVacancies !== undefined)
    params.append("hasVacancies", hasVacancies.toString());
  if (timeFilter) params.append("timeFilter", timeFilter);
  params.append("size", pageSize.toString());
  params.append("page", currentPage.toString());

  const { data: eventsData, isLoading } = useQuery({
    queryKey: [
      "events",
      "admin",
      selectedStatuses,
      debouncedCompanyName,
      debouncedContactName,
      hasVacancies,
      timeFilter,
      startDateFrom,
      startDateTo,
      endDateFrom,
      endDateTo,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      const queryString = params.toString();
      const response = await axiosInstance.get(
        `event/admin${queryString ? `?${queryString}` : ""}`,
      );

      // Update pagination state
      if (response.data.page?.totalPages !== undefined) {
        setTotalPages(response.data.page.totalPages);
      }

      return (
        response.data.content?.map((event: AdminEventSummaryDTO): Bid => ({
          id: event.id,
          contactName: event.contactName,
          companyName: event.companyName,
          startTime: new Date(event.startTime),
          bidStatus: (event.status as EventStatusEnum) || EventStatusEnum.NEW,
        })) || []
      );
    },
    meta: {
      onError: (error) => {
        toast({
          title: "Error",
          description: "Unable to fetch events.",
          variant: "destructive",
        });
      },
    },
  });

  const handleSortColumn = useCallback((column: keyof Bid) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const sortedBids = useMemo(() => {
    const bids = eventsData || [];
    if (!bids.length || !sortColumn) return bids;

    return [...bids].sort((a: Bid, b: Bid) => {
      if (sortColumn === "startTime") {
        const timeA = new Date(a.startTime).getTime();
        const timeB = new Date(b.startTime).getTime();
        return sortDirection === "asc" ? timeA - timeB : timeB - timeA;
      }

      const valA = a[sortColumn];
      const valB = b[sortColumn];

      if (typeof valA === "string" && typeof valB === "string") {
        return sortDirection === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      return sortDirection === "asc"
        ? valA > valB
          ? 1
          : -1
        : valA < valB
          ? 1
          : -1;
    });
  }, [eventsData, sortColumn, sortDirection]);

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold text-foreground mb-8">Manage Bids</h1>

      <EventFilterComponent
        selectedStatuses={selectedStatuses}
        companyName={companyName}
        contactName={contactName}
        hasVacancies={hasVacancies}
        timeFilter={timeFilter}
        startDateFrom={startDateFrom}
        startDateTo={startDateTo}
        endDateFrom={endDateFrom}
        endDateTo={endDateTo}
        onStatusesChange={setSelectedStatuses}
        onCompanyNameChange={setCompanyName}
        onContactNameChange={setContactName}
        onHasVacanciesChange={setHasVacancies}
        onTimeFilterChange={setTimeFilter}
        onStartDateFromChange={setStartDateFrom}
        onStartDateToChange={setStartDateTo}
        onEndDateFromChange={setEndDateFrom}
        onEndDateToChange={setEndDateTo}
        allowedStatuses={MANAGE_BIDS_CONFIG.allowedStatuses}
        showStatusFilter={MANAGE_BIDS_CONFIG.showStatusFilter}
        showTimeFilter={MANAGE_BIDS_CONFIG.showTimeFilter}
        showVacancyFilter={MANAGE_BIDS_CONFIG.showVacancyFilter}
        showDateFilters={MANAGE_BIDS_CONFIG.showDateFilters}
        showClientFilters={MANAGE_BIDS_CONFIG.showClientFilters}
        showCompanyNameFilter={MANAGE_BIDS_CONFIG.showCompanyNameFilter}
      />

      {isLoading ? (
        <ActiveBidsTableSkeleton />
      ) : (
        <div className="rounded-md border">
          <ActiveBidsTable
            bids={sortedBids}
            onSortColumn={handleSortColumn}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
          />
        </div>
      )}

      <DataTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
}
